import {useEffect, useMemo, useState, useRef, useCallback} from 'react';
import {
  addNewsletterPopupShowed,
  addWelcomePopupShowed,
  checkActiveCampaign,
  checkShowNewsletterPopup,
  checkShowWelcomePopup,
  addCampaignPopupShowed
} from '@scripttag/helpers/storage';
import {
  WELCOME_POPUP_ADVANCED_DISPLAY_TYPE,
  WELCOME_POPUP_ADVANCED_TRIGGER_TYPE
} from '@functions/const/defaultWidgetSetting';
import {
  DEFAULT_CAMPAIGN_TRIGGER_TYPE,
  DEFAULT_CAMPAIGN_DURATION
} from '@assets/helpers/campaign/defaultCampaignSetting';
import getScrollPercent from '@assets/helpers/utils/getScrollPercent';

// Throttle utility to limit scroll event handling
const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

const DEFAULT_TEASER_DISPLAY_TIME = 8;

/**
 * Decide when to display teaser pop-ups, based on time elapsed or user's scroll percent
 */
export default function useDisplayPopups({
  settings,
  setOpenWelcomePopup,
  setOpenNewsletterPopup,
  campaigns,
  setActiveCampaigns
}) {
  const cartTracking = localStorage.getItem('avada-chatty/cart-tracking');
  const activeCampaigns = useMemo(() => checkActiveCampaign(campaigns), [campaigns, cartTracking]);

  // ---------------------------- Old Automation Handling Logic ----------------------------
  if (activeCampaigns.length === 0) {
    const {advancedSettings} = settings.welcomePopupSetting;
    const {trigger, display} = advancedSettings;
    const isDisplayWelcomePopup = useMemo(() => checkShowWelcomePopup(), []);
    const isDisplayNewsletterPopup = useMemo(() => checkShowNewsletterPopup(), []);
    const alwaysShowWelcomePopup = useMemo(
      () => display.type === WELCOME_POPUP_ADVANCED_DISPLAY_TYPE.ALWAYS,
      []
    );

    const welcomePopupTriggerDelay =
      trigger.type === WELCOME_POPUP_ADVANCED_TRIGGER_TYPE.AFTER_TIME ? trigger.value * 1000 : 0;
    const newsletterPopupTriggerDelay = isDisplayWelcomePopup
      ? welcomePopupTriggerDelay + (display.value ?? 0) * 1000
      : 0; // display.value is undefined when alwaysShowWelcomePopup = true, thus this value is unnecessary

    const [triggerDisplayPopups, setTriggerDisplayPopups] = useState(
      trigger.type === WELCOME_POPUP_ADVANCED_TRIGGER_TYPE.AFTER_TIME
    ); // when true, begin showing popups

    const [scrollPercent, setScrollPercent] = useState(0);

    // add on scroll event listener
    useEffect(() => {
      const handleScroll = () => {
        setScrollPercent(getScrollPercent());
      };
      document.addEventListener('scroll', handleScroll);
      return () => {
        document.removeEventListener('scroll', handleScroll);
      };
    }, []);

    // display welcome popup
    useEffect(() => {
      if (!isDisplayWelcomePopup || !triggerDisplayPopups) return;
      const closePopup = () => {
        addWelcomePopupShowed();
        setOpenWelcomePopup(false);
      };

      const showPopupTimeout = setTimeout(() => {
        setOpenWelcomePopup(true);
        if (!alwaysShowWelcomePopup)
          setTimeout(() => {
            closePopup();
          }, display.value * 1000); // close popup
      }, welcomePopupTriggerDelay);

      return () => clearTimeout(showPopupTimeout);
    }, [triggerDisplayPopups]);

    // display newsletter popup
    useEffect(() => {
      if (
        !isDisplayNewsletterPopup ||
        (isDisplayWelcomePopup && alwaysShowWelcomePopup) ||
        !triggerDisplayPopups
      )
        return; // never show if welcome popup keep showing
      const closePopup = () => {
        addNewsletterPopupShowed();
        setOpenNewsletterPopup(false);
      };
      const showPopupTimeout = setTimeout(() => {
        setOpenWelcomePopup(false); // close previous pop-ups
        setOpenNewsletterPopup(true);
        setTimeout(() => closePopup(), DEFAULT_TEASER_DISPLAY_TIME * 1000);
      }, newsletterPopupTriggerDelay);
      return () => clearTimeout(showPopupTimeout);
    }, [triggerDisplayPopups]);

    // listen to scroll percent to trigger display
    useEffect(() => {
      if (trigger.type !== WELCOME_POPUP_ADVANCED_TRIGGER_TYPE.AFTER_SCROLL) return;
      if (scrollPercent >= trigger.value) {
        setTriggerDisplayPopups(true);
      }
    }, [scrollPercent]);
  }

  // ---------------------------- Campaign Handling Logic ----------------------------
  const [displayedCampaigns, setDisplayedCampaigns] = useState([]);
  const campaignTimeouts = useRef({});
  const scrollPercentRef = useRef(0);

  // Update parent when displayed campaigns change
  useEffect(() => {
    setActiveCampaigns([...displayedCampaigns]);
  }, [displayedCampaigns, setActiveCampaigns]);

  // Memoize scroll trigger campaigns
  const scrollTriggerCampaigns = useMemo(
    () =>
      activeCampaigns.filter(c => c?.trigger?.type === DEFAULT_CAMPAIGN_TRIGGER_TYPE.AFTER_SCROLL),
    [activeCampaigns]
  );

  // Memoize scroll handler with throttling
  const handleScroll = useCallback(
    throttle(() => {
      const newScrollPercent = getScrollPercent();
      scrollPercentRef.current = newScrollPercent;

      scrollTriggerCampaigns.forEach(campaign => {
        if (!campaign?.id || !campaign?.trigger?.value) return;

        const {id, trigger, condition} = campaign;
        const timeouts = campaignTimeouts.current || {};

        if (
          timeouts[`${id}_trigger_fired`] ||
          timeouts[`${id}_duration`] ||
          displayedCampaigns.some(c => c.id === id)
        )
          return;

        if (newScrollPercent >= trigger.value) {
          console.log(`Scroll trigger hit for campaign: ${id}`);
          timeouts[`${id}_trigger_fired`] = true;

          setDisplayedCampaigns(prev => {
            if (prev.some(c => c.id === id)) return prev;
            const newCampaigns = [...prev, campaign];

            // Handle duration if needed
            if (condition?.duration?.type === DEFAULT_CAMPAIGN_DURATION.CUSTOM) {
              const durationValue =
                condition.duration.value && !isNaN(condition.duration.value)
                  ? condition.duration.value
                  : 10;
              const durationMs = durationValue * 1000;
              timeouts[`${id}_duration`] = setTimeout(() => {
                setDisplayedCampaigns(current => current.filter(c => c.id !== id));
                addCampaignPopupShowed([id]);
                delete timeouts[`${id}_duration`];
              }, durationMs);
            }

            return newCampaigns;
          });
        }
      });
    }, 100),
    [scrollTriggerCampaigns, displayedCampaigns]
  );

  // Add scroll listener only if needed
  useEffect(() => {
    if (!scrollTriggerCampaigns.length) return;

    document.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Effect to manage time-based triggers
  useEffect(() => {
    const timeouts = campaignTimeouts.current || {};

    activeCampaigns.forEach(campaign => {
      if (!campaign?.id || !campaign?.trigger || !campaign?.condition) return;

      const {id, trigger, condition} = campaign;

      clearTimeout(timeouts[`${id}_trigger`]);
      clearTimeout(timeouts[`${id}_duration`]);

      if (trigger.type === DEFAULT_CAMPAIGN_TRIGGER_TYPE.AFTER_TIME) {
        const delayValue = trigger.value && !isNaN(trigger.value) ? trigger.value : 5;
        const delayMs = delayValue * 1000;

        timeouts[`${id}_trigger`] = setTimeout(() => {
          setDisplayedCampaigns(prev => {
            if (prev.some(c => c.id === id)) return prev;
            const newCampaigns = [...prev, campaign];

            if (condition.duration?.type === DEFAULT_CAMPAIGN_DURATION.CUSTOM) {
              const durationValue =
                condition.duration.value && !isNaN(condition.duration.value)
                  ? condition.duration.value
                  : 10;
              const durationMs = durationValue * 1000;
              timeouts[`${id}_duration`] = setTimeout(() => {
                setDisplayedCampaigns(current => current.filter(c => c.id !== id));
                addCampaignPopupShowed([id]);
                delete timeouts[`${id}_duration`];
              }, durationMs);
            }

            return newCampaigns;
          });
        }, delayMs);
      }
    });

    return () => {
      Object.keys(timeouts).forEach(key => {
        clearTimeout(timeouts[key]);
        delete timeouts[key];
      });
    };
  }, [activeCampaigns]);
}
